import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import audioBuzzAPI from '../../services/audioBuzzApi';

const BatchAnalysis = ({ onBatchComplete, onError }) => {
  const [files, setFiles] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [analysisOptions, setAnalysisOptions] = useState({
    thresholdStrong: -45.0,
    stdThreshold1: 15.0,
    stdThreshold2: 14.0,
    outlierCountThreshold: 7
  });

  const onDrop = useCallback((acceptedFiles) => {
    const validFiles = acceptedFiles.filter(file => 
      file.name.toLowerCase().endsWith('.wav') || 
      file.name.toLowerCase().endsWith('.txt')
    );
    setFiles(prev => [...prev, ...validFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/wav': ['.wav'],
      'text/plain': ['.txt']
    },
    multiple: true
  });

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearFiles = () => {
    setFiles([]);
  };

  const handleBatchAnalysis = async () => {
    if (files.length === 0) return;

    setIsAnalyzing(true);
    setProgress({ current: 0, total: files.length });

    try {
      const options = { ...analysisOptions };
      const batchResult = await audioBuzzAPI.analyzeBatch(files, options);
      
      onBatchComplete(batchResult.results);
    } catch (error) {
      onError(error.message);
    } finally {
      setIsAnalyzing(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const handleOptionChange = (key, value) => {
    setAnalysisOptions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-white mb-2">📁 Batch Analysis</h2>
        <p className="text-gray-400">Upload multiple WAV or TXT files for simultaneous analysis</p>
      </div>

      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
        <h3 className="text-lg font-semibold text-white mb-4">Analysis Parameters</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Strong Threshold (dB)</label>
            <input
              type="number"
              step="0.1"
              value={analysisOptions.thresholdStrong}
              onChange={(e) => handleOptionChange('thresholdStrong', parseFloat(e.target.value))}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Std Threshold 1</label>
            <input
              type="number"
              step="0.1"
              value={analysisOptions.stdThreshold1}
              onChange={(e) => handleOptionChange('stdThreshold1', parseFloat(e.target.value))}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Std Threshold 2</label>
            <input
              type="number"
              step="0.1"
              value={analysisOptions.stdThreshold2}
              onChange={(e) => handleOptionChange('stdThreshold2', parseFloat(e.target.value))}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">Outlier Count Threshold</label>
            <input
              type="number"
              value={analysisOptions.outlierCountThreshold}
              onChange={(e) => handleOptionChange('outlierCountThreshold', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200 ${
          isDragActive 
            ? 'border-blue-500 bg-gray-800' 
            : 'border-gray-700 hover:border-gray-600 hover:bg-gray-900'
        }`}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <div className="space-y-2">
            <div className="text-4xl">📂</div>
            <p className="text-blue-400 font-medium">Drop the files here...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-5xl">📂</div>
            <div className="space-y-2">
              <p className="text-lg font-medium text-white">
                Drag & drop multiple WAV or TXT files here, or click to select
              </p>
              <p className="text-sm text-gray-400">
                Supported formats: .wav, .txt
              </p>
            </div>
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-white">Selected Files ({files.length})</h3>
            <div className="flex gap-3">
              <button 
                className="px-4 py-2 text-red-400 bg-red-900/30 hover:bg-red-900/50 rounded-lg font-medium transition-colors duration-200 border border-red-800"
                onClick={clearFiles}
              >
                🗑️ Clear All
              </button>
              <button 
                className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                  isAnalyzing 
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
                onClick={handleBatchAnalysis}
                disabled={isAnalyzing}
              >
                {isAnalyzing ? '⏳ Analyzing...' : '🔍 Analyze All'}
              </button>
            </div>
          </div>

          {isAnalyzing && (
            <div className="mb-6">
              <div className="bg-gray-800 rounded-full h-2 mb-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-400 text-center">
                Analyzing {progress.current} of {progress.total} files...
              </p>
            </div>
          )}

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {files.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <span className="text-2xl">
                      {file.name.toLowerCase().endsWith('.wav') ? '🎵' : '📄'}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white truncate">{file.name}</p>
                    <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      file.name.toLowerCase().endsWith('.wav') 
                        ? 'bg-blue-900/50 text-blue-300 border border-blue-700' 
                        : 'bg-green-900/50 text-green-300 border border-green-700'
                    }`}>
                      {file.name.toLowerCase().endsWith('.wav') ? 'WAV' : 'TXT'}
                    </span>
                  </div>
                </div>
                <button 
                  className={`ml-3 p-1 rounded-full transition-colors duration-200 ${
                    isAnalyzing 
                      ? 'text-gray-600 cursor-not-allowed' 
                      : 'text-red-400 hover:bg-red-900/30'
                  }`}
                  onClick={() => removeFile(index)}
                  disabled={isAnalyzing}
                >
                  ❌
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchAnalysis;
